import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import UploadZone from '../UploadZone'
import Uppy from '@uppy/core'
import { useEffect, useRef, useState } from 'react'
import { ContainerParam, ContainerType, FileSystemItem } from '@features/subject/subject.types'
import {
  deleteItemsByIds,
  getAllChildrenIdsForParents,
  getUploadFileName,
  mapFilesToStructure,
} from '@features/subject/utils'
import { useParams } from 'react-router-dom'

type AddFilesStepProps = {
  uppy: Uppy
  containerParam: ContainerParam[]
  onCheckStepComplete: (bool: boolean) => void
}

const getFileTypeAllowed = (data: Array<ContainerParam>, type: ContainerType): string[] => {
  const container = data.find((item) => item.type === type)
  return container ? container.fileTypesAllowed : []
}

// upload types that can't be derived by the generic underscore-split rule
export const FILE_TYPE_EXTENSION_MAP: Record<string, string[]> = {
  VIDEO_MP4: ['.mp4'],
}

export const convertListFileTypes = (data: string[]): string[] => {
  const result = data.flatMap(
    (fileType) => FILE_TYPE_EXTENSION_MAP[fileType] ?? fileType.split('_').map((part) => `.${part.toLowerCase()}`),
  )

  // Add `.dcm` to the result by default
  if (!result.includes('.dcm')) {
    result.push('.dcm')
  }

  return result
}

export default function AddFilesStep({ uppy, containerParam, onCheckStepComplete }: AddFilesStepProps) {
  const [data, setData] = useState<FileSystemItem[]>(mapFilesToStructure(uppy.getFiles()))
  const [isPreparingFiles, setIsPreparingFiles] = useState(false)
  const isPreparingFilesRef = useRef(false)

  const { studyId, timepointSubmissionId, subjectId } = useParams<{
    studyId: string
    subjectId: string
    timepointSubmissionId: string
  }>()

  const fileTypeAllowed = getFileTypeAllowed(containerParam, 'NON_DICOM')
  const listFileTypes = convertListFileTypes(fileTypeAllowed)

  const onRemove = (filesIds: string[]) => {
    const actualFiles = getAllChildrenIdsForParents(data, filesIds)
    uppy.removeFiles(actualFiles)
    setData(deleteItemsByIds(data, actualFiles))
  }

  // Shows the loading overlay, then adds files on the next tick so the overlay can paint first.
  const addFilesWithLoader = (addFiles: () => void) => {
    isPreparingFilesRef.current = true
    setIsPreparingFiles(true)

    setTimeout(() => {
      try {
        addFiles()
        setData(mapFilesToStructure(uppy.getFiles()))
      } finally {
        isPreparingFilesRef.current = false
        setIsPreparingFiles(false)
      }
    }, 0)
  }

  const onDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) return

    addFilesWithLoader(() => {
      acceptedFiles.forEach((file) => {
        const fileId = uppy.addFile(file)
        const relativePath =
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          file.path || file.name

        uppy.setFileMeta(fileId, {
          path: relativePath,
          file_name: getUploadFileName(file.name, relativePath),
          file_type: file.type || 'application/octet-stream',
          study_id: studyId,
          subject_id: subjectId,
          timepoint_submission_id: timepointSubmissionId,
        })
      })
    })
  }

  // Handle folder selection using native file input
  const handleFolderSelect = () => {
    const input = document.createElement('input')
    input.type = 'file'
    input.webkitdirectory = true
    input.multiple = true
    input.style.display = 'none'

    input.onchange = (e) => {
      const files = (e.target as HTMLInputElement).files
      const fileArray = files ? Array.from(files) : []
      document.body.removeChild(input)

      if (!fileArray.length) return

      addFilesWithLoader(() => {
        fileArray.forEach((file) => {
          try {
            const relativePath = file.webkitRelativePath || file.name
            const fileId = uppy.addFile({
              name: file.name,
              type: file.type,
              data: file,
              meta: {
                relativePath,
                path: relativePath,
                file_name: getUploadFileName(file.name, relativePath),
                file_type: file.type || 'application/octet-stream',
                study_id: studyId,
                subject_id: subjectId,
                timepoint_submission_id: timepointSubmissionId,
              },
            })
            console.log('Added file:', file.name, 'with ID:', fileId)
          } catch (error) {
            console.error('Error adding file:', file.name, error)
          }
        })
      })
    }

    document.body.appendChild(input)
    input.click()
  }

  // Skip per-file updates during bulk adds; the tree is rebuilt once when adding finishes.
  useEffect(() => {
    const syncData = () => {
      if (isPreparingFilesRef.current) return
      setData(mapFilesToStructure(uppy.getFiles()))
    }

    uppy.on('file-added', syncData)
    uppy.on('file-removed', syncData)

    return () => {
      uppy.off('file-added', syncData)
      uppy.off('file-removed', syncData)
    }
  }, [uppy])

  useEffect(() => {
    onCheckStepComplete(data.length > 0)
  }, [data.length])

  return (
    <Box>
      <Typography color="text.primary" variant="h4">
        Add and review files
      </Typography>
      <Typography color="text.secondary" variant="body2" mb={4}>
        Upload a complete imaging dataset folder, or add individual files if needed
      </Typography>

      <Box position="relative">
        <UploadZone
          onRemove={onRemove}
          data={data}
          onDrop={onDrop}
          onDropRejected={() => {}}
          fileTypesAllowed={listFileTypes}
          accept={{ '*/*': [] }}
          onFolderSelect={handleFolderSelect}
        />
        {isPreparingFiles && (
          <Stack
            alignItems="center"
            justifyContent="center"
            spacing={1}
            sx={{ position: 'absolute', inset: 0, bgcolor: 'background.paper', opacity: 0.9, zIndex: 1 }}
          >
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary">
              Preparing files...
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  )
}
